import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import useLanguageContext from '../../hooks/useLanguageContext';
import SupportedLanguages from '../../setup/intl/enums';
import {
  IconBank,
  IconCash,
  IconChevronLeft,
  IconChevronRight,
  IconLocation,
  IconTick,
  IconWarehouse,
} from '../../assets/svg/icons';
import ScCard from '../../components/atoms/ScCard';
import ScSectionHeader from '../../components/molecules/ScSectionHeader';
import ScButton from '../../components/atoms/ScButton';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import Badge from '../../components/atoms/Badge';
import { BadgeTone } from '../../components/atoms/Badge/enums';
import { CartLine, DeliverTo, PaymentMethod, QuoteView } from './types';
import { iqd } from './format';
import { cartSubtotal, tierPrice } from './pricing';
import { unitForms } from './liveData';
import { ProductImage } from './components';
import {
  StyledBackChip,
  StyledBreadcrumb,
  StyledCrumbCurrent,
  StyledCrumbSep,
  StyledErrorState,
  StyledOrderGrid,
  StyledStickyCol,
} from './styles';
import {
  StyledAddressField,
  StyledCheckoutLeft,
  StyledDivider,
  StyledLegal,
  StyledRadioCard,
  StyledRadioCircle,
  StyledRadioGrid,
  StyledRadioIcon,
  StyledRadioStack,
  StyledRadioText,
  StyledRecapHeader,
  StyledRecapLine,
  StyledRecapThumb,
  StyledStep,
  StyledStepDot,
  StyledStepLabel,
  StyledStepLine,
  StyledStepRow,
  StyledSummaryNote,
  StyledSumRow,
} from './styles2';

interface ScreenCheckoutProps {
  lines: CartLine[];
  quote?: QuoteView;
  quoteLoading?: boolean;
  quoteError?: boolean;
  placing: boolean;
  error: boolean;
  onBack: () => void;
  onPlaceOrder: (args: {
    paymentMethod: PaymentMethod;
    deliverTo: DeliverTo;
    deliverAddress?: string;
  }) => void;
}

interface RadioCardProps {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  sub: string;
  icon: React.ReactNode;
  rightTag?: { label: string; gold?: boolean };
}

const RadioCard: React.FC<RadioCardProps> = ({ active, disabled, onClick, title, sub, icon, rightTag }) => (
  <StyledRadioCard type="button" $active={active} disabled={disabled} aria-pressed={active} onClick={onClick} style={disabled ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}>
    <StyledRadioCircle $active={active}>{active && <span />}</StyledRadioCircle>
    <StyledRadioIcon $active={active}>{icon}</StyledRadioIcon>
    <StyledRadioText>
      <span>{title}</span>
      <span>{sub}</span>
    </StyledRadioText>
    {rightTag && <Badge tone={rightTag.gold ? BadgeTone.Warning : BadgeTone.Success}>{rightTag.label}</Badge>}
  </StyledRadioCard>
);

const ScreenCheckout: React.FC<ScreenCheckoutProps> = ({
  lines,
  quote,
  quoteLoading,
  quoteError,
  placing,
  error,
  onBack,
  onPlaceOrder,
}) => {
  const intl = useIntl();
  const { language } = useLanguageContext();
  const isAr = language === SupportedLanguages.Arabic;
  const cur = intl.formatMessage({ id: 'marketplace.currency', defaultMessage: 'IQD' });
  // Freight + total are SERVER-OWNED (quote.freightIqd / quote.totalIqd).
  // Subtotal is client-reliable; the server quote is the cross-check.
  const subtotal = quote ? quote.subtotalIqd : cartSubtotal(lines);
  const hasQuote = !!quote;
  const perMerchant = quote ? quote.merchants.length > 1 : false;
  const dash = intl.formatMessage({ id: 'marketplace.freight.loading', defaultMessage: '…' });
  const freightDisplay = hasQuote
    ? quote!.freightIqd === 0
      ? intl.formatMessage({ id: 'marketplace.freight.free', defaultMessage: 'Free delivery' })
      : `${iqd(quote!.freightIqd)} ${cur}`
    : dash;
  const totalDisplay = hasQuote
    ? `${iqd(quote!.totalIqd)} ${cur}`
    : quoteError
    ? `${iqd(subtotal)} ${cur}`
    : dash;
  const [pay, setPay] = useState<PaymentMethod>('wallet');
  const [dest, setDest] = useState<DeliverTo>('sandoog_hub');
  const [address, setAddress] = useState('');

  const canPlace = lines.length > 0 && (dest === 'sandoog_hub' || address.trim().length > 0);

  return (
    <>
      <StyledBreadcrumb>
        <StyledBackChip type="button" onClick={onBack}>
          <IconChevronLeft />
          <span>{intl.formatMessage({ id: 'marketplace.cart.title', defaultMessage: 'Cart' })}</span>
        </StyledBackChip>
        <StyledCrumbSep><IconChevronRight /></StyledCrumbSep>
        <StyledCrumbCurrent>{intl.formatMessage({ id: 'marketplace.checkout', defaultMessage: 'Checkout' })}</StyledCrumbCurrent>
      </StyledBreadcrumb>

      {/* stepper */}
      <StyledStepRow>
        <StyledStep>
          <StyledStepDot $done><IconTick /></StyledStepDot>
          <StyledStepLabel>{intl.formatMessage({ id: 'marketplace.step.cart', defaultMessage: 'Cart' })}</StyledStepLabel>
        </StyledStep>
        <StyledStepLine $done />
        <StyledStep>
          <StyledStepDot $active className="tnum">2</StyledStepDot>
          <StyledStepLabel $active>{intl.formatMessage({ id: 'marketplace.checkout', defaultMessage: 'Checkout' })}</StyledStepLabel>
        </StyledStep>
        <StyledStepLine />
        <StyledStep>
          <StyledStepDot className="tnum">3</StyledStepDot>
          <StyledStepLabel>{intl.formatMessage({ id: 'marketplace.step.confirmed', defaultMessage: 'Confirmed' })}</StyledStepLabel>
        </StyledStep>
      </StyledStepRow>

      <StyledOrderGrid $side="380px">
        <StyledCheckoutLeft>
          {/* deliver to */}
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader title={intl.formatMessage({ id: 'marketplace.deliverTo', defaultMessage: 'Deliver to' })} icon={<IconLocation />} />
            <StyledRadioGrid>
              <RadioCard
                active={dest === 'sandoog_hub'}
                onClick={() => setDest('sandoog_hub')}
                title={intl.formatMessage({ id: 'marketplace.deliver.hub', defaultMessage: 'Sandoog hub' })}
                sub={intl.formatMessage({ id: 'marketplace.deliver.hubSub', defaultMessage: 'Recommended · auto-receive into warehouse' })}
                icon={<IconWarehouse />}
              />
              <RadioCard
                active={dest === 'direct'}
                onClick={() => setDest('direct')}
                title={intl.formatMessage({ id: 'marketplace.deliver.direct', defaultMessage: 'Direct to my address' })}
                sub={intl.formatMessage({ id: 'marketplace.deliver.directSub', defaultMessage: 'Your registered address' })}
                icon={<IconLocation />}
              />
            </StyledRadioGrid>
            {dest === 'direct' && (
              <StyledAddressField
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={intl.formatMessage({ id: 'marketplace.deliver.addressPlaceholder', defaultMessage: 'Street, district, city — delivery address' })}
                aria-label={intl.formatMessage({ id: 'marketplace.deliver.address', defaultMessage: 'Delivery address' })}
              />
            )}
          </ScCard>

          {/* payment */}
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader title={intl.formatMessage({ id: 'marketplace.paymentMethod', defaultMessage: 'Payment method' })} icon={<IconBank />} />
            <StyledRadioStack>
              <RadioCard
                active={pay === 'wallet'}
                onClick={() => setPay('wallet')}
                icon={<IconCash />}
                title={intl.formatMessage({ id: 'marketplace.pay.wallet', defaultMessage: 'Sandoog wallet' })}
                sub={intl.formatMessage({ id: 'marketplace.pay.walletSub', defaultMessage: 'Pilot account balance: 4,750,000 IQD' })}
                rightTag={{ label: intl.formatMessage({ id: 'marketplace.pay.noFees', defaultMessage: 'No fees' }) }}
              />
              <RadioCard
                active={pay === 'bank_transfer'}
                onClick={() => setPay('bank_transfer')}
                icon={<IconBank />}
                title={intl.formatMessage({ id: 'marketplace.pay.transfer', defaultMessage: 'Bank transfer' })}
                sub={intl.formatMessage({ id: 'marketplace.pay.transferSub', defaultMessage: "We'll send account details after you place the order" })}
              />
              {/* net-30 hidden for pilot — cash-only */}
            </StyledRadioStack>
          </ScCard>

          {/* items recap */}
          <ScCard pad={false}>
            <StyledRecapHeader>{intl.formatMessage({ id: 'marketplace.itemsN', defaultMessage: '{n, plural, one {# item} other {# items}}' }, { n: lines.length })}</StyledRecapHeader>
            {lines.map(({ product: p, qty }, i) => {
              const lineUnitForms = unitForms(p.stockUnit, language);
              return (
                <StyledRecapLine key={p.id} $divided={i < lines.length - 1}>
                  <StyledRecapThumb $hue={p.hue}><ProductImage product={p} glyphScale={0.45} /></StyledRecapThumb>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>{isAr ? p.nameAr || p.name : p.name}</div>
                    <div className="muted tnum" style={{ fontSize: 11 }}>{intl.formatMessage({ id: 'marketplace.countUnit', defaultMessage: '{n, plural, one {# {unit}} other {# {unitPlural}}}' }, { n: qty, ...lineUnitForms })} × {iqd(tierPrice(p, qty))} {cur}</div>
                  </div>
                  <div className="mono tnum" style={{ fontWeight: 700, fontSize: 'var(--t-sm)' }}>{iqd(tierPrice(p, qty) * qty)}</div>
                </StyledRecapLine>
              );
            })}
          </ScCard>
        </StyledCheckoutLeft>

        {/* summary */}
        <StyledStickyCol>
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader title={intl.formatMessage({ id: 'marketplace.summary', defaultMessage: 'Summary' })} />
            <StyledSumRow><span>{intl.formatMessage({ id: 'marketplace.subtotalItems', defaultMessage: 'Subtotal · {n, plural, one {# item} other {# items}}' }, { n: lines.length })}</span><span className="mono tnum">{iqd(subtotal)} {cur}</span></StyledSumRow>
            <StyledSumRow aria-busy={quoteLoading && !hasQuote ? true : undefined}>
              <span>{intl.formatMessage({ id: 'marketplace.freight', defaultMessage: 'Freight' })}</span>
              <span className="mono tnum">{freightDisplay}</span>
            </StyledSumRow>
            {quoteError && !hasQuote && (
              <StyledSummaryNote className="muted">{intl.formatMessage({ id: 'marketplace.freight.atCheckout', defaultMessage: 'Freight calculated at checkout' })}</StyledSummaryNote>
            )}
            {perMerchant && (
              <StyledSummaryNote className="muted tnum">{intl.formatMessage({ id: 'marketplace.freight.perPo', defaultMessage: 'Freight is charged per supplier · {n} purchase orders' }, { n: quote!.merchants.length })}</StyledSummaryNote>
            )}
            <StyledSumRow><span>{intl.formatMessage({ id: 'marketplace.importHandling', defaultMessage: 'Import & handling' })}</span><span>{intl.formatMessage({ id: 'marketplace.included', defaultMessage: 'Included' })}</span></StyledSumRow>
            <StyledDivider />
            <StyledSumRow $big><span>{intl.formatMessage({ id: 'marketplace.totalDue', defaultMessage: 'Total due' })}</span><span className="mono tnum">{totalDisplay}</span></StyledSumRow>

            {error && (
              <StyledErrorState role="alert" style={{ padding: 'var(--s-4) 0 0' }}>
                <span>{intl.formatMessage({ id: 'marketplace.orderFailed', defaultMessage: "Couldn't place your order. Please try again." })}</span>
              </StyledErrorState>
            )}

            <ScButton kind={ButtonKind.Primary} size={ButtonSize.Large} full icon={IconTick} style={{ marginTop: 'var(--s-4)' }} disabled={!canPlace || placing}
              onClick={() => onPlaceOrder({ paymentMethod: pay, deliverTo: dest, deliverAddress: dest === 'direct' ? address.trim() : undefined })}>
              {placing
                ? intl.formatMessage({ id: 'marketplace.placing', defaultMessage: 'Placing order…' })
                : intl.formatMessage({ id: 'marketplace.placeOrder', defaultMessage: 'Place order' })}
            </ScButton>
            <StyledLegal>{intl.formatMessage({ id: 'marketplace.legal', defaultMessage: "By placing this order you agree to Sandoog's wholesale terms and the supplier's lead times." })}</StyledLegal>
          </ScCard>
        </StyledStickyCol>
      </StyledOrderGrid>
    </>
  );
};

export default ScreenCheckout;
